// On-demand native browser location only. No IP lookup or fabricated coordinates.
export const locationFailure = (code, message) => Object.assign(new Error(message), { code });

export const isEmbeddedLocationContext = () => {
  try { return window.self !== window.top; } catch { return true; }
};

export const isIOSLocationDevice = () => /iPad|iPhone|iPod/.test(navigator.userAgent || '') ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const locationAvailability = ({ embedded = isEmbeddedLocationContext() } = {}) => {
  if (!window.isSecureContext) return locationFailure('insecure', 'Location requires HTTPS. Open the secure preview link.');
  if (!navigator.geolocation) return locationFailure('unsupported', 'This browser does not provide device location. Open this page in Safari or Chrome on your phone.');
  if (embedded) return locationFailure('embedded', 'iPhone location prompts may be blocked inside the embedded preview. Open this preview in a new Safari tab, then tap Ping phone location again.');
  const policy = document.permissionsPolicy || document.featurePolicy;
  if (policy?.allowsFeature && !policy.allowsFeature('geolocation')) return locationFailure('embedded', 'Location is blocked by this embedded preview. Open the preview in a new tab, then allow location.');
  return null;
};

export const collectDeviceLocation = ({ signal, onUpdate, accuracyLimit, timeoutMs = 45000, iosDevice = isIOSLocationDevice() }) => new Promise((resolve, reject) => {
  let finished = false;
  let retryTimer;
  let deadline;
  let firstResponseTimer;
  const readings = [];
  const finish = error => {
    if (finished) return;
    finished = true; clearTimeout(retryTimer); clearTimeout(deadline); clearTimeout(firstResponseTimer);
    signal.removeEventListener('abort', abort);
    if (error) reject(error); else resolve(readings);
  };
  const abort = () => finish(new DOMException('GPS capture cancelled', 'AbortError'));
  const unavailable = locationAvailability();
  if (unavailable) { finish(unavailable); return; }
  if (signal.aborted) { abort(); return; }
  signal.addEventListener('abort', abort, { once: true });
  const update = (message, fix = null) => onUpdate({ message, progress: readings.length, fix });
  update('Requesting phone location… Allow location if your browser asks.');
  deadline = setTimeout(() => finish(locationFailure('timeout', iosDevice
    ? 'The iPhone did not return a location. Open this preview directly in Safari, allow Location and Precise Location for this site, then retry.'
    : 'Location capture timed out. Check phone Location Services and precise-location permission, stand outdoors, then retry.')), timeoutMs);
  if (iosDevice) firstResponseTimer = setTimeout(() => finish(locationFailure('permission-timeout',
    'No location permission response came from this iPhone. Open the preview directly in Safari, then allow Location and Precise Location for this site.')), Math.min(20000, timeoutMs));
  const request = () => {
    if (finished) return;
    try {
      navigator.geolocation.getCurrentPosition(position => {
        if (finished) return;
        clearTimeout(firstResponseTimer);
        const { latitude, longitude, accuracy } = position.coords || {};
        const timestamp = position.timestamp;
        if (![latitude, longitude, accuracy, timestamp].every(Number.isFinite) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180 || accuracy <= 0) {
          update('The phone returned an invalid location. Requesting a fresh reading…');
        } else {
          const fix = { latitude, longitude, accuracy, timestamp: Math.round(timestamp) };
          if (Date.now() - timestamp > 10000 || timestamp - Date.now() > 5000) update('The phone returned an old location. Waiting for a fresh reading…', fix);
          else if (accuracy > accuracyLimit) update(`Phone located · ±${Math.ceil(accuracy)} m. Waiting for accuracy within ${accuracyLimit} m; stand outdoors with precise location enabled.`, fix);
          else if (readings.length && timestamp - readings[readings.length - 1].timestamp < 1000) update('Phone located. Waiting for the next distinct reading…', fix);
          else {
            readings.push(fix);
            update(readings.length === 3 ? 'Phone location captured · 3 of 3 readings.' : `Phone located · ${readings.length} of 3 readings · ±${Math.ceil(accuracy)} m.`, fix);
            if (readings.length === 3) { finish(); return; }
          }
        }
        // Stationary phones may emit only one watchPosition event. Explicit fresh
        // requests also work when the user stands still over the pin.
        retryTimer = setTimeout(request, 1100);
      }, error => {
        if (finished) return;
        clearTimeout(firstResponseTimer);
        if (error.code === 1) finish(locationFailure('denied', 'Location permission was denied. Allow location for this site in browser settings and enable your phone’s Location Services, then retry.'));
        else if (error.code === 3 && iosDevice && readings.length === 0) finish(locationFailure('permission-timeout', 'The iPhone did not provide a location permission response. Open the preview directly in Safari and allow Location and Precise Location, then retry.'));
        else {
          update(error.code === 2 ? 'The phone cannot determine a location yet. Check Location Services and move outdoors…' : 'Still waiting for the phone’s location…');
          retryTimer = setTimeout(request, 1100);
        }
      }, { enableHighAccuracy: true, maximumAge: 0, timeout: 12000 });
    } catch {
      finish(locationFailure('blocked', 'The browser blocked the location request. Open the preview directly in Safari or Chrome and allow location.'));
    }
  };
  request();
});